import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FolderKanban, Plus, DollarSign, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [coDialog, setCoDialog] = useState(false);
  const [expDialog, setExpDialog] = useState(false);
  const [coForm, setCoForm] = useState({ title: '', description: '', total: 0 });
  const [expForm, setExpForm] = useState({ vendor_name: '', amount: 0, category: '', date: '', notes: '' });
  const [jobCosting, setJobCosting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = () => api.get('/projects').then(r => setProjects(r.data)).catch(() => {});

  const loadProject = async (id) => {
    const r = await api.get(`/projects/${id}`);
    setSelected(r.data);
    const jc = await api.get(`/projects/${id}/job-costing`);
    setJobCosting(jc.data);
  };

  const updateStage = async (idx, status) => {
    const stages = [...selected.stages];
    stages[idx] = { ...stages[idx], status };
    await api.put(`/projects/${selected.id}`, { stages });
    loadProject(selected.id);
    loadProjects();
    toast.success('Stage updated');
  };

  const createCO = async () => {
    await api.post(`/projects/${selected.id}/change-orders`, coForm);
    toast.success('Change order created');
    setCoDialog(false);
    setCoForm({ title: '', description: '', total: 0 });
    loadProject(selected.id);
    loadProjects();
  };

  const addExpense = async () => {
    await api.post(`/projects/${selected.id}/expenses`, expForm);
    toast.success('Expense recorded');
    setExpDialog(false);
    setExpForm({ vendor_name: '', amount: 0, category: '', date: '', notes: '' });
    loadProject(selected.id);
    loadProjects();
  };

  const stageIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (status === 'in_progress') return <Clock className="w-4 h-4 text-amber-500" />;
    return <div className="w-4 h-4 rounded-full border-2 border-slate-300" />;
  };

  if (selected) return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="project-detail-page">
      <Button variant="ghost" onClick={() => setSelected(null)} className="mb-4 text-slate-500" data-testid="back-to-projects-btn">Back to Projects</Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">{selected.name}</h1>
          <Badge className={selected.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{selected.status}</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setCoDialog(true)} data-testid="add-change-order-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Change Order
          </Button>
          <Button size="sm" variant="outline" onClick={() => setExpDialog(true)} data-testid="add-expense-btn">
            <DollarSign className="w-3.5 h-3.5 mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-base">Construction Stages</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {selected.stages?.map((stage, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {stageIcon(stage.status)}
                  <span className="text-sm font-medium text-slate-700">{stage.name}</span>
                </div>
                <Select value={stage.status} onValueChange={v => updateStage(idx, v)}>
                  <SelectTrigger className="h-7 w-32 text-xs" data-testid={`stage-status-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {jobCosting && (
            <Card className="border-slate-100">
              <CardHeader><CardTitle className="text-base">Job Costing</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-sky-50 rounded-lg text-center">
                    <p className="text-lg font-bold font-mono text-sky-700">${(jobCosting.revised_contract || 0).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Contract</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-lg font-bold font-mono text-orange-700">${(jobCosting.actual_cost || 0).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Actual Cost</p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${(jobCosting.forecasted_gross_profit || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <p className={`text-lg font-bold font-mono ${(jobCosting.forecasted_gross_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      ${(jobCosting.forecasted_gross_profit || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Forecasted Profit ({(jobCosting.forecasted_margin_pct || 0).toFixed(0)}%)</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(jobCosting.breakdown || []).map(cat => (
                    <div key={cat.category} className="flex items-center justify-between text-sm py-1">
                      <span className="capitalize text-slate-600">{cat.category}</span>
                      <div className="flex gap-4 font-mono text-xs">
                        <span className="text-slate-500">${(cat.estimated || cat.planned || 0).toLocaleString()}</span>
                        <span className="text-slate-800">${(cat.actual || 0).toLocaleString()}</span>
                        <span className={(cat.variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                          {(cat.variance || 0) >= 0 ? '+' : ''}{(cat.variance || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {selected.change_orders?.length > 0 && (
            <Card className="border-slate-100">
              <CardHeader><CardTitle className="text-base">Change Orders</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {selected.change_orders.map(co => (
                  <div key={co.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg" data-testid={`change-order-${co.id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{co.co_number} — {co.title}</p>
                      <p className="text-xs text-slate-400">{co.scope_description || co.description || ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">${(co.requested_price_impact || co.total || 0).toLocaleString()}</span>
                      <Badge className={co.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{co.status}</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={coDialog} onOpenChange={setCoDialog}>
        <DialogContent data-testid="change-order-dialog">
          <DialogHeader><DialogTitle>New Change Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={coForm.title} onChange={e => setCoForm(p => ({...p, title: e.target.value}))} data-testid="co-title" /></div>
            <div><Label>Description</Label><Textarea value={coForm.description} onChange={e => setCoForm(p => ({...p, description: e.target.value}))} data-testid="co-desc" /></div>
            <div><Label>Total ($)</Label><Input type="number" value={coForm.total} onChange={e => setCoForm(p => ({...p, total: parseFloat(e.target.value) || 0}))} data-testid="co-total" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoDialog(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={createCO} data-testid="co-save-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={expDialog} onOpenChange={setExpDialog}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Record Expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Vendor</Label><Input value={expForm.vendor_name} onChange={e => setExpForm(p => ({...p, vendor_name: e.target.value}))} data-testid="exp-vendor" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount ($)</Label><Input type="number" value={expForm.amount} onChange={e => setExpForm(p => ({...p, amount: parseFloat(e.target.value) || 0}))} data-testid="exp-amount" /></div>
              <div><Label>Category</Label><Input value={expForm.category} onChange={e => setExpForm(p => ({...p, category: e.target.value}))} data-testid="exp-category" /></div>
            </div>
            <div><Label>Date</Label><Input type="date" value={expForm.date} onChange={e => setExpForm(p => ({...p, date: e.target.value}))} data-testid="exp-date" /></div>
            <div><Label>Notes</Label><Textarea value={expForm.notes} onChange={e => setExpForm(p => ({...p, notes: e.target.value}))} rows={2} data-testid="exp-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpDialog(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={addExpense} data-testid="exp-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="projects-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Projects</h1>
          <p className="text-slate-500 text-sm mt-1">{projects.length} projects</p>
        </div>
      </div>
      <div className="space-y-3">
        {projects.map(p => (
          <Card key={p.id} className="border-slate-100 hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadProject(p.id)} data-testid={`project-card-${p.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-sky-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{p.name}</p>
                  <p className="text-xs text-slate-400">{p.stages?.filter(s => s.status === 'completed').length || 0}/{p.stages?.length || 0} stages complete</p>
                </div>
              </div>
              <Badge className={p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{p.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {projects.length === 0 && (
          <div className="text-center py-12">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No projects yet. Win a bid to create one!</p>
          </div>
        )}
      </div>
    </div>
  );
}
